import { useState, useEffect, useRef } from 'react';

import PropTypes from 'prop-types';
import { BsPlus, BsList } from 'react-icons/bs';

import TextInput from '#globalComponents/Forms/TextInput';

import { MAPS_SEARCH_INDUSTRY_CATEGORIES } from '../../../constants/industryCategories';

/**
 * Comma-separated industry/search-terms input with a suggestion dropdown.
 * Free text is allowed; suggestions are prefix-filtered from the shared category list.
 * Mirrors the sidebar's IndustryFilterGroup behaviour, decoupled for the popup.
 */
export const IndustryTermsInput = ({ value, onChange }) => {
	const [input, setInput] = useState(value || '');
	const [isOpen, setIsOpen] = useState(false);
	const [filterText, setFilterText] = useState('');
	const ref = useRef(null);

	useEffect(() => {
		if (value !== undefined && value !== input) setInput(value);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [value]);

	useEffect(() => {
		const handler = (e) => {
			if (ref.current && !ref.current.contains(e.target)) setIsOpen(false);
		};
		document.addEventListener('mousedown', handler);
		return () => document.removeEventListener('mousedown', handler);
	}, []);

	const filtered = MAPS_SEARCH_INDUSTRY_CATEGORIES.filter((industry) => {
		if (!filterText.trim()) return true;
		return industry.toLowerCase().startsWith(filterText.toLowerCase());
	});

	const handleChange = (next) => {
		setInput(next);
		const terms = next.split(',');
		const lastTerm = terms[terms.length - 1].trim();
		setFilterText(lastTerm);
		if (lastTerm.length > 0) setIsOpen(true);
		onChange(next);
	};

	const addFromDropdown = (industry) => {
		const list = input
			.split(',')
			.map((x) => x.trim())
			.filter(Boolean);
		if (list.includes(filterText)) list.pop();
		if (!list.includes(industry)) list.push(industry);
		const next = list.join(', ');
		setInput(next);
		onChange(next);
		setFilterText('');
	};

	return (
		<div className="relative" ref={ref}>
			<label htmlFor="criteria-industry-input" className="block text-xs text-bgBase-800 mb-1">
				Suchbegriffe <small className="text-bgBase-800">(kommagetrennt, Freitext möglich)</small>
			</label>
			<div className="relative">
				<TextInput
					id="criteria-industry-input"
					placeholder="z.B. CNC-Fertigung"
					value={input}
					onChange={(e) => handleChange(e.target.value)}
					onFocus={() => setIsOpen(true)}
					className="pr-9"
				/>
				<button
					type="button"
					onClick={() => {
						setIsOpen(!isOpen);
						if (!isOpen) setFilterText('');
					}}
					className="absolute right-1 top-1/2 -translate-y-1/2 p-1 bg-gray-100 rounded hover:bg-gray-200"
					title="Vorschläge anzeigen"
				>
					<BsList className="w-3.5 h-3.5 text-text" />
				</button>
			</div>

			{isOpen && (
				<div className="absolute z-50 w-full mt-1 bg-white border border-bgBase-500 rounded-card shadow-elevated max-h-64 overflow-y-auto">
					{filterText && (
						<div className="sticky top-0 bg-primary-100 px-2 py-1 text-xs text-primary-700 border-b border-primary-200">
							Filter: &quot;{filterText}&quot; ({filtered.length})
						</div>
					)}
					<div className="py-1">
						{filtered.length > 0 ? (
							filtered.map((industry) => {
								const isActive = input
									.split(',')
									.map((x) => x.trim())
									.includes(industry);
								return (
									<button
										key={industry}
										type="button"
										onClick={() => addFromDropdown(industry)}
										className={`w-full text-left px-2 py-1.5 text-xs flex items-center transition-colors ${isActive ? 'bg-yellow-50 text-caution-700 font-medium' : 'hover:bg-gray-50 text-text'}`}
									>
										<BsPlus
											className={`mr-1 flex-shrink-0 ${isActive ? 'opacity-100' : 'opacity-50'}`}
										/>
										<span>{industry}</span>
									</button>
								);
							})
						) : (
							<div className="px-2 py-3 text-xs text-bgBase-800 text-center">
								Keine Vorschläge gefunden
							</div>
						)}
					</div>
				</div>
			)}
		</div>
	);
};

IndustryTermsInput.propTypes = {
	value: PropTypes.string,
	onChange: PropTypes.func.isRequired,
};
